/**
 * useEditorSetup — OutpostForge frontend (CES standard)
 * Loads everything the slotting editor needs for a scope: assets, the
 * hardware-supported types (asset scope), and the active capacity task that
 * would block this scope per the documented parallelism rule (an
 * Outpost-level task blocks everything; an asset-level task blocks only the
 * same asset; outpost scope is blocked by any active task).
 */

import { useEffect, useState } from 'react';
import {
  capacityTaskRepository,
  type CapacityTaskRepository,
} from '../repositories/CapacityTaskRepository';
import { outpostRepository, type OutpostRepository } from '../repositories/OutpostRepository';
import { describeApiError } from '../services/apiErrors';
import type { AssetSummary, CapacityTaskSummary } from '../types/outposts';
import { ACTIVE_TASK_STATUSES } from '../types/outposts';

export function useEditorSetup(
  outpostId: string,
  assetId: string | undefined,
  repositories: {
    outposts?: OutpostRepository;
    tasks?: CapacityTaskRepository;
  } = {},
) {
  const outposts = repositories.outposts ?? outpostRepository;
  const tasks = repositories.tasks ?? capacityTaskRepository;
  const isAssetScope = assetId !== undefined;

  const [assets, setAssets] = useState<AssetSummary[]>([]);
  const [supportedNames, setSupportedNames] = useState<string[] | null>(null);
  const [activeTask, setActiveTask] = useState<CapacityTaskSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      outposts.getAssets(outpostId),
      isAssetScope ? outposts.getInstanceTypes(outpostId, assetId) : Promise.resolve(null),
      tasks.list({ outpostId, statuses: ACTIVE_TASK_STATUSES }),
    ])
      .then(([assetsResult, typesResult, activeTasks]) => {
        if (cancelled) return;
        setAssets(assetsResult);
        if (typesResult?.supported) {
          setSupportedNames(typesResult.supported.map((t) => t.instanceType));
        }
        const blocker = activeTasks.find(
          (task) => !isAssetScope || task.assetId === undefined || task.assetId === assetId,
        );
        setActiveTask(blocker ?? null);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(describeApiError(err));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [outpostId, assetId, isAssetScope, outposts, tasks]);

  return { assets, supportedNames, activeTask, loading, loadError };
}
